import { useMemo, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../hooks';
import { showCustomToast } from '../../../utils';
import { copyToClipboard } from 'components/ui/utils';
import {
  IConstant,
  INotificationParams,
  INotificationSendResult,
} from '../../../types';
import {
  ChevronIcon,
  Content,
  Item,
  Separator,
  Trigger,
} from './payment-button.styled';
import { alertHollow, checkCircle } from '../../../assets/icons';
import { Button } from '../../ui';

export const PaymentButton = observer(() => {
  const { createRequest, tenants, constants } = useStore();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const activeChannels: IConstant[] = useMemo(() => {
    return constants.requests.NotificationConstants.filter(
      (item: IConstant) => tenants.communicationChannels[item.value]
    );
  }, [constants.requests.NotificationConstants, tenants.communicationChannels]);

  const sendPayment = (value: string): void => {
    const params: INotificationParams = {
      channel: value,
      tenant_id: createRequest.applicant.memberId,
      sectors: tenants.tenantStats.sectors,
      area: createRequest.selectedArea,
    };

    tenants.postNotificationSend(params).then(
      (resolve: INotificationSendResult[]) => {
        showCustomToast(
          Array.isArray(resolve) && resolve[0]?.status === 'success'
            ? {
                title: 'Ссылка на оплату отправлена',
                icon: checkCircle,
              }
            : {
                title: 'Ссылка на оплату не отправлена',
                subtitle: 'Попробуйте позже',
                icon: alertHollow,
              }
        );
      },
      (reject) => {
        showCustomToast({
          title: 'Ссылка на оплату не отправлена',
          subtitle: 'Попробуйте позже',
          icon: alertHollow,
        });
      }
    );
  };

  const copyPaymentLink = (): void => {
    const params: INotificationParams = {
      channel: 'link',
      tenant_id: createRequest.applicant.memberId,
      sectors: tenants.tenantStats.sectors,
      area: createRequest.selectedArea,
    };

    tenants.postNotificationSend(params).then(
      (resolve: INotificationSendResult[]) => {
        const links = Array.isArray(resolve)
          ? resolve
              .filter((item) => item?.status === 'success' && item?.link)
              .map((item) => item.link)
          : [];

        if (!links.length) {
          showCustomToast({
            title: 'Не удалось получить ссылку на оплату',
            subtitle: 'Попробуйте позже',
            icon: alertHollow,
          });

          return;
        }

        copyToClipboard(links.join('\n'), false, {
          success: 'Ссылка на оплату скопирована',
          error: 'Не удалось скопировать ссылку',
        });
      },
      (reject) => {
        showCustomToast({
          title: 'Не удалось получить ссылку на оплату',
          subtitle: 'Попробуйте позже',
          icon: alertHollow,
        });
      }
    );
  };

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <Trigger>
        <Button
          mode="additional"
          variant="main"
          size="m"
          rightIcon={<ChevronIcon isOpen={!isOpen} />}
        >
          Отправить ссылку на оплату
        </Button>
      </Trigger>

      <DropdownMenu.Portal>
        <Content isOpen={isOpen} side={'top'} sideOffset={5}>
          {activeChannels.map((item: IConstant, index: number) => (
            <Item key={index} onClick={() => sendPayment(item.value)}>
              {item.text}
            </Item>
          ))}

          {!!activeChannels.length && <Separator />}

          <Item onClick={copyPaymentLink}>Скопировать ссылку на оплату</Item>
        </Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
});
